import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useState } from 'react';

/** Replace with your WhatsApp business number (country code + number, no + or spaces). Example: 919876543210 */
const WHATSAPP_NUMBER = '12394395123';
const WHATSAPP_BASE_URL = `https://wa.me/${WHATSAPP_NUMBER}`;

function WhatsAppIcon({ className, size = 28 }: { className?: string; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={cn('shrink-0', className)}
      fill="currentColor"
      aria-hidden
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function openWhatsApp(message: string) {
  const url = `${WHATSAPP_BASE_URL}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function WhatsAppChatWidget() {
  const [open, setOpen] = useState(false);

  const handleExistingClient = () => {
    openWhatsApp('Hi, I am an existing client and would like to get in touch.');
    setOpen(false);
  };

  const handleNewClient = () => {
    openWhatsApp('Hi, I am interested in becoming a new client.');
    setOpen(false);
  };

  const handleChatOnWhatsApp = () => {
    openWhatsApp('Hi, I would like to chat with CenTax Filer.');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-all  hover:bg-[#20bd5a] hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-[#25D366]/50"
          aria-label="Open WhatsApp chat"
        >
          <WhatsAppIcon size={32} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="top"
        sideOffset={12}
        className="w-[340px] overflow-hidden rounded-2xl border-0 p-0 shadow-xl"
      >
        {/* Header */}
        <div className="bg-zinc-900 px-4 py-4">
          <div className="flex items-start gap-3">
            {/* Logo placeholder */}
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-lg font-bold">
              {/* <img src="/dark-logo.png" alt="CenTax Filer" className="h-8 w-auto dark:hidden" /> */}
              <img src="/logo.png" alt="CenTax Filer" className="h-8 w-auto dark:block" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-amber-400">CenTax Filer</h3>
              <p className="mt-0.5 text-sm text-zinc-400">
                We will reply as soon as possible
              </p>
            </div>
          </div>
        </div>

        {/* Body with chat-style message */}
        <div className="relative bg-[#e5ddd5] px-4 py-4">
          {/* Subtle pattern overlay */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
          <div className="relative">
            <div className="rounded-2xl rounded-tl-sm bg-white px-4 py-3 shadow-sm">
              <p className="font-semibold text-amber-600">CenTax Filer</p>
              <p className="mt-1 text-sm text-zinc-700">Hi there!</p>
              <p className="text-sm text-zinc-700">How can I help you?</p>
            </div>

            {/* Action buttons */}
            <div className="mt-4 flex flex-col gap-2">
              <Button
                onClick={handleExistingClient}
                className="w-full bg-[#25D366] font-medium text-white hover:bg-[#20bd5a]"
              >
                Existing Client(s)
              </Button>
              <Button
                onClick={handleNewClient}
                className="w-full bg-[#25D366] font-medium text-white hover:bg-[#20bd5a]"
              >
                New Client(s)
              </Button>
              <Button
                onClick={handleChatOnWhatsApp}
                className="w-full gap-2 bg-[#25D366] font-medium text-white hover:bg-[#20bd5a]"
              >
                <WhatsAppIcon size={20} className="text-white" />
                Chat on WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
